import { useMainContext } from '@/context/main-context';
import { formatNumber } from '@/lib/utils';
import { Users } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

function GroupCard({ card }) {
  const { teacherData, students, courses } = useMainContext();
  const groupStudents = students.filter((student) =>
    card?.students?.includes(student.id)
  );

  const teacherFullName = teacherData?.fullName;

  const course = courses?.find((item) => item.id === card?.courseId);
  const courseTitle = course?.courseTitle || 'Kurs topilmadi';
  const groupNumber = card?.groupNumber || '';
  const displayName = groupNumber
    ? `${courseTitle} #${groupNumber}`
    : courseTitle;

  const getInitials = (fullName) => {
    const nameParts = fullName.split(' ');

    if (nameParts.length === 1) {
      return nameParts[0].slice(0, 2).toUpperCase();
    } else {
      return nameParts[0][0]?.toUpperCase() + nameParts[1][0]?.toUpperCase();
    }
  };

  return (
    <Card
      key={card.id}
      className="bg-muted dark:bg-background group-card group-dark-card"
    >
      <div className="p-4 pb-0 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 overflow-x-auto">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge
                    className="whitespace-nowrap"
                    variant={card.dayType === 'odd' ? 'odd' : 'even'}
                  >
                    {card.dayType === 'odd' ? 'Toq kunlar' : 'Juft kunlar'}{' '}
                    {card.timeInDay}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="text-xs font-medium text-accent-foreground">
                    {card.dayType === 'odd'
                      ? 'Du - Chor - Ju'
                      : card.dayType === 'even'
                        ? 'Se - Pay - Shan'
                        : card.dayType === 'custom'
                          ? 'Boshqa kunlar'
                          : 'Har kuni'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Badge variant={card.status ? 'active' : 'inactive'}>
              {card.status ? 'Aktiv' : 'Tugatildi'}
            </Badge>

            <Badge
              className="bg-white dark:bg-muted flex items-center gap-1.5 w-fit text-xs"
              variant="secondary"
            >
              <Users className="w-3 h-3" />
              <span>
                {card?.students?.length
                  ? ` ${card?.students?.length} ta`
                  : ' 0'}
              </span>
            </Badge>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg lg:text-xl font-semibold">
              {displayName}
            </h2>
          </div>
          {groupStudents?.length === 0 && (
            <p className="text-muted-foreground text-sm mt-1 md:mt-2">
              O'quvchi qo'shilmagan
            </p>
          )}
          <div className="flex items-center -space-x-2.5 mt-2 md:mt-3">
            {groupStudents.slice(0, 5).map((student) => (
              <Avatar
                title={student?.fullName}
                key={student.id}
                className="w-7 h-7 shadow border border-border"
              >
                <AvatarImage src="" />
                <AvatarFallback className="text-xs bg-white dark:bg-muted">
                  {getInitials(student?.fullName)}
                </AvatarFallback>
              </Avatar>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between py-3 px-4 mt-4 border-t border-border">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Avatar className="h-8 w-8">
            <AvatarImage
              src=""
              alt={
                teacherFullName
                  ? teacherFullName
                  : 'Ustoz topilmadi - yangi ustoz tanlang'
              }
            />
            <AvatarFallback className="text-xs bg-white dark:bg-muted">
              {teacherFullName
                ?.split(' ')
                .map((word) => word[0])
                .join('')
                ?.slice(0, 2)
                .toUpperCase() || 'UT'}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium truncate min-w-0">
            {teacherFullName ? teacherFullName : 'Ustoz tanlanmagan'}
          </span>
        </div>
      </div>
    </Card>
  );
}

export default GroupCard;
